<template>
  <MainHeader />
  <MainSidebar />
  <div class="main-content bg_gray d-flex flex-column transition overflow-hidden">
    <BreadcrumbMenu :pageTitle="pageTitle" />

    <div class="card border-0 shadow-none rounded-1 mb-25">
      <div class="card-body p-xl-40">
        <div class="row g-3 align-items-center">
          <div class="col-lg-8">
            <div class="d-flex flex-wrap align-items-center gap-2">
              <button
                type="button"
                class="btn style-two px-4 h-55"
                data-bs-toggle="modal"
                data-bs-target="#userReportFilterModal"
              >
                {{ $t(`${i18nScope}.filter`) }}
                <i class="ri-filter-line ms-1"></i>
              </button>

              <div class="search-box position-relative">
                <input
                  v-model="searchQuery"
                  type="text"
                  :placeholder="$t(`${i18nScope}.search_placeholder`)"
                  class="form-control bg-white h-55 shadow-none pe-5"
                />
                <span class="search-icon">
                  <i class="ri-search-line"></i>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <UserReport
      :active-tab="activeTab"
      :items="currentState.items"
      :loading="currentState.loading"
      :pagination="pagination"
      :summary="summary"
      :i18n-scope="i18nScope"
      @tab-change="setActiveTab"
      @table-change="handleTableChange"
    />

    <div class="flex-grow-1"></div>
    <MainFooter />
  </div>

  <div class="modal fade" id="userReportFilterModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content border-0 shadow">
        <div class="modal-header border-bottom-dashed">
          <h5 class="modal-title fw-bold">{{ $t(`${i18nScope}.advanced_filter`) }}</h5>
          <button
            type="button"
            class="btn-close shadow-none"
            data-bs-dismiss="modal"
            :aria-label="$t(`${i18nScope}.close`)"
          ></button>
        </div>
        <div class="modal-body">
          <div class="row g-3">
            <div class="col-12">
              <label class="form-label fs-14 text-title">{{ $t(`${i18nScope}.user`) }}</label>
              <select class="form-select bg-light border-0 fs-14 shadow-none" v-model="filters.user_id">
                <option :value="null">{{ $t(`${i18nScope}.all_users`) }}</option>
                <option v-for="user in users" :key="user.id" :value="user.id">
                  {{ formatUserLabel(user) }}
                </option>
              </select>
            </div>
            <div class="col-12">
              <label class="form-label fs-14 text-title">{{ $t(`${i18nScope}.warehouse`) }}</label>
              <select class="form-select bg-light border-0 fs-14 shadow-none" v-model="filters.warehouse_id">
                <option :value="null">{{ $t(`${i18nScope}.all_warehouses`) }}</option>
                <option v-for="warehouse in warehouses" :key="warehouse.id" :value="warehouse.id">
                  {{ warehouse.name }}
                </option>
              </select>
            </div>
            <div class="col-md-6">
              <label class="form-label fs-14 text-title">{{ $t(`${i18nScope}.from_date`) }}</label>
              <input v-model="filters.from_date" type="date" class="form-control bg-light border-0 fs-14 shadow-none" />
            </div>
            <div class="col-md-6">
              <label class="form-label fs-14 text-title">{{ $t(`${i18nScope}.to_date`) }}</label>
              <input v-model="filters.to_date" type="date" class="form-control bg-light border-0 fs-14 shadow-none" />
            </div>
          </div>
        </div>
        <div class="modal-footer border-top-dashed justify-content-between">
          <button type="button" class="btn btn-light" @click="resetFilters">
            {{ $t(`${i18nScope}.reset`) }}
          </button>
          <button type="button" class="btn btn-primary" data-bs-dismiss="modal" @click="applyFilters">
            {{ $t(`${i18nScope}.apply_filter`) }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { useI18n } from "vue-i18n";

import MainHeader from "../../components/Layouts/MainHeader.vue";
import MainSidebar from "../../components/Layouts/MainSidebar.vue";
import BreadcrumbMenu from "../../components/Common/BreadcrumbMenu.vue";
import UserReport from "../../components/Reports/UserReport/UserReport.vue";
import MainFooter from "../../components/Layouts/MainFooter.vue";
import { useUserReport } from "../../composables/useUserReport";

export default defineComponent({
  name: "UserReportPage",
  components: {
    MainHeader,
    MainSidebar,
    BreadcrumbMenu,
    UserReport,
    MainFooter,
  },
  setup() {
    const { t } = useI18n();
    const report = useUserReport();

    const formatUserLabel = (user: { first_name?: string; last_name?: string; username?: string }) => {
      const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
      return fullName || user.username || t(`${report.i18nScope}.unknown_user`);
    };

    return {
      ...report,
      formatUserLabel,
    };
  },
});
</script>

<style scoped>
.search-box {
  width: min(100%, 420px);
}

.search-box .form-control {
  padding-left: 18px;
  border: 1px solid #e6ebf2;
  border-radius: 8px;
  background: #fff;
  color: #1f2940;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.04);
}

.search-box .form-control:focus {
  border-color: #5d5fef;
  box-shadow: 0 0 0 3px rgba(93, 95, 239, 0.12);
}

.search-icon {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #8f9bb3;
  pointer-events: none;
  font-size: 18px;
}

.border-bottom-dashed,
.border-top-dashed {
  border-color: #e7ebf3 !important;
  border-style: dashed !important;
}
</style>
